import type { Absence, TimelineSlot } from '../types';
import {
  type AbsenceEntry,
  type AbsenceTimeline,
  entriesReaching,
} from './blocks';

/**
 * The entry a draft is editing, read once, when the draft opens.
 *
 * Read once on purpose. The timeline refetches on every absence-changed
 * message, on socket connect and whenever the tab comes back into view, so
 * resolving the entry at submission time would hand an untouched draft
 * whatever shape the last refetch brought -- a fresh revision for days the
 * user never saw, and with it a token the server has no way to refuse. The
 * whole point of the token is to describe what was on screen when the edit was
 * written, so it is taken then and held.
 *
 * `entries` holds the entry each named day belonged to *at that moment*. Per
 * day rather than one entry for the draft, because which days are in question
 * depends on the type the form ends up submitting -- a reshape is the whole
 * entry, a retype is the days named -- and that is not known until the user
 * presses Request.
 *
 * `absences` are the rows of those entries as they read then: what the
 * confirmation dialogs describe, and what the revision covers.
 */
export type EditScope = {
  userId: number | null;
  /** Which draft this belongs to, so a new selection is not this one. */
  draftId: number | null;
  /** Whether the pane named these days, or a drag was read as reshaping them. */
  named: boolean;
  namedIds: number[];
  entries: ReadonlyMap<number, AbsenceEntry>;
  absences: Absence[];
  /** Named days this response could not place. Nothing may be submitted. */
  missing: number[];
};

/**
 * What goes out with a submission.
 *
 * `ids` becomes `edited_absence_ids` and `revision` becomes `block_revision`,
 * and they belong to each other: the server resolves the entry from the ids
 * and refuses a token taken from a different one. Both empty reads as "work
 * the scope out from the days I asked for", which is the older route and still
 * right for a booking that is not an edit of anything.
 *
 * `conflict` is set where the draft cannot be sent at all. Nothing is written,
 * and the user is told -- as against quietly sending something narrower, which
 * is how an edit of one entry came to answer for another.
 */
export type SubmitScope = {
  ids: number[];
  absences: Absence[];
  revision: string | null;
  missing: number[];
  conflict: 'spans-entries' | null;
};

const NOTHING: SubmitScope = {
  ids: [],
  absences: [],
  revision: null,
  missing: [],
  conflict: null,
};

/**
 * The ids a submission is about: the ones the pane opened, or failing that the
 * ones a drag covered.
 *
 * Taken as they are and never rebuilt from the rows in hand. The calendar
 * holds a window, and moving about it takes rows out of memory without taking
 * the entry out of the edit -- rebuilt, the ids would come back empty, the
 * server would read a reshape as a fresh booking, and the days being moved
 * away from would quietly stay where they are.
 */
export function namedAbsenceIds(
  slot: TimelineSlot | null,
  { infer }: { infer: boolean },
): number[] {
  const opened =
    slot?.editedAbsenceIds ??
    (slot?.editedAbsenceId ? [slot.editedAbsenceId] : []);
  if (opened.length) return opened;
  return infer ? (slot?.selectedIds ?? []) : [];
}

/** Whether the pane named its days, as against a drag having been read. */
function wasNamed(slot: TimelineSlot | null): boolean {
  return !!slot?.editedAbsenceIds?.length || !!slot?.editedAbsenceId;
}

/**
 * Read what a draft is editing out of the response in hand.
 *
 * The partition is the server's. The client used to work it out for itself, by
 * grouping the rows it had loaded, and that is the divergence this replaces: a
 * bar can hold two questions, the grouping that draws it cannot tell them
 * apart, and an edit scoped to the bar answered both.
 *
 * Called on every render and held by :func:`nextEditScope`, which keeps the
 * first reading that placed every named day. Returns `null` only where there
 * is no draft at all.
 */
export function captureEditScope(
  slot: TimelineSlot | null,
  absences: readonly Absence[],
  timeline: Pick<AbsenceTimeline, 'entryByAbsenceId'>,
  { infer }: { infer: boolean },
): EditScope | null {
  if (!slot) return null;

  const namedIds = namedAbsenceIds(slot, { infer });
  const base = {
    userId: slot.userId,
    draftId: slot.draftId ?? null,
    named: wasNamed(slot),
    namedIds,
  };
  if (!namedIds.length) {
    return { ...base, entries: new Map(), absences: [], missing: [] };
  }

  const { missing } = entriesReaching(timeline, namedIds);
  const byDay = new Map<number, AbsenceEntry>();
  for (const id of namedIds) {
    const entry = timeline.entryByAbsenceId.get(id);
    if (entry) byDay.set(id, entry);
  }
  // `missing` is days that could not be *placed*, and nothing else. A member
  // with no row in hand is a gap in the rows, not a gap in the reading: the
  // entry and its revision are already known, and topping the rows up later
  // must not be allowed to reach the token. :func:`scopeForSubmission` reports
  // the shortfall for the entry it actually picks, so a drag that turns out to
  // be a booking over the top never pays for rows it has no use for.
  return {
    ...base,
    entries: byDay,
    absences: rowsOf(byDay, absences),
    missing,
  };
}

/** The rows of these entries that are in hand, in the order given. */
function rowsOf(
  entries: ReadonlyMap<number, AbsenceEntry>,
  absences: readonly Absence[],
): Absence[] {
  const wanted = new Set<number>();
  for (const entry of entries.values()) {
    for (const memberId of entry.memberIds) wanted.add(memberId);
  }
  return absences.filter((absence) => wanted.has(absence.id));
}

/**
 * The same reading, with its rows topped up from a later response.
 *
 * What an edit is scoped to -- the entries, and so the ids and the token --
 * is untouched. Only the rows change, and they are what the dialogs read: a
 * day nothing can be said about is a day whose removal cannot be described.
 * This is how a draft whose entry reaches outside the window is completed
 * without being re-read.
 */
export function withRows(
  scope: EditScope,
  absences: readonly Absence[],
): EditScope {
  return { ...scope, absences: rowsOf(scope.entries, absences) };
}

/**
 * Whether looking again could tell this scope anything more.
 *
 * A settled scope is frozen: refetches must not touch it. An unsettled one is
 * a draft that opened before its days were in hand -- the pane can open from a
 * deep link ahead of the query -- and there the *first* successful reading is
 * the one to keep, because there was no earlier shape for it to be stale
 * against.
 */
export function isSettledScope(scope: EditScope): boolean {
  return scope.namedIds.length === 0 || scope.missing.length === 0;
}

/**
 * Keep the reading a draft opened with; take a new one only for a new draft.
 *
 * The one rule that makes the revision mean anything. A refetch brings a new
 * `candidate` describing the same draft, and answering with it would give an
 * untouched draft a token for days it never showed.
 */
export function nextEditScope(
  current: EditScope | null,
  candidate: EditScope | null,
): EditScope | null {
  if (!candidate) return null;
  if (!current) return candidate;
  if (current.draftId !== candidate.draftId) return candidate;
  if (current.userId !== candidate.userId) return candidate;
  if (current.named !== candidate.named) return candidate;
  // A guard on the draft id, not a second identity. A gesture adjusting the
  // open draft is about the edit in hand, so the days it names are days that
  // edit already had: dragging one event's edge names that day, and a draft
  // open on the entry it belongs to names it among its own. Anything reaching
  // outside them is a different edit, whatever it did with the draft id.
  if (current.named && !namesWithin(candidate.namedIds, current.namedIds)) {
    return candidate;
  }
  return isSettledScope(current) ? current : candidate;
}

function namesWithin(
  inner: readonly number[],
  outer: readonly number[],
): boolean {
  const held = new Set(outer);
  return inner.every((id) => held.has(id));
}

/** The distinct entries a set of days belonged to, in the order first seen. */
function entriesOf(scope: EditScope, rows: readonly Absence[]): AbsenceEntry[] {
  const reached: AbsenceEntry[] = [];
  for (const row of rows) {
    const entry = scope.entries.get(row.id);
    if (entry && !reached.includes(entry)) reached.push(entry);
  }
  return reached;
}

/**
 * What to submit, from the scope the draft opened with and the type chosen.
 *
 * The rules, in order:
 *
 * - Nothing named: a booking, which is not an edit of anything.
 * - A day that could not be placed: nothing is decided, and `missing` says
 *   which. The caller fetches those days by id and captures again.
 * - Days of the submitted type among those named: a reshape, so the whole
 *   entry goes -- its days outside the requested range are the ones being
 *   given up, which is exactly what the server has to be told.
 * - None of them: a retype. The days named are what changes type and are not
 *   widened to their entry, because the days around them are not changing.
 * - Days belonging to more than one entry: one token cannot answer two
 *   questions. A drag falls back to letting the server work each entry out
 *   from the days asked for; a draft that *named* its days stops instead,
 *   because dropping the ids would drop the scope it was checked against --
 *   an entry's outlying days would be left behind and neither the revision nor
 *   the membership guard would ever see the submission.
 */
export function scopeForSubmission(
  scope: EditScope | null,
  typeId: unknown,
): SubmitScope {
  if (!scope?.namedIds.length) return NOTHING;
  if (scope.missing.length) {
    return { ...NOTHING, missing: scope.missing };
  }

  const named = new Set(scope.namedIds);
  const namedRows = scope.absences.filter((absence) => named.has(absence.id));
  if (namedRows.length !== scope.namedIds.length) {
    // Placed but not in hand. Whether this is a reshape or a retype is read
    // off the rows themselves, so deciding it from the ones that happen to be
    // loaded would read a day out of the window as a day of another type.
    const held = new Set(namedRows.map((absence) => absence.id));
    return {
      ...NOTHING,
      missing: scope.namedIds.filter((id) => !held.has(id)),
    };
  }
  const reshaped = namedRows.filter(
    (absence) => absence.allDay && absence.typeId === typeId,
  );
  const reached = entriesOf(scope, reshaped.length ? reshaped : namedRows);
  if (reached.length !== 1) {
    return scope.named ? { ...NOTHING, conflict: 'spans-entries' } : NOTHING;
  }
  const entry = reached[0];

  if (!reshaped.length) {
    // A drag landing only on other types is a booking over the top of them,
    // which is what it looks like to the server and what it should be.
    if (!scope.named) return NOTHING;
    return {
      ids: [...scope.namedIds],
      absences: namedRows,
      revision: entry.revision,
      missing: [],
      conflict: null,
    };
  }

  const members = entry.memberIds
    .map((id) => scope.absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => absence !== undefined);
  if (members.length !== entry.memberIds.length) {
    const held = new Set(members.map((absence) => absence.id));
    return {
      ...NOTHING,
      missing: entry.memberIds.filter((id) => !held.has(id)),
    };
  }
  return {
    ids: [...entry.memberIds],
    absences: members,
    revision: entry.revision,
    missing: [],
    conflict: null,
  };
}
